import { Injectable, inject } from '@angular/core';
import { ROUTES } from '@core/constants';
import {
  CustomerPortalOrderPhoto,
  CustomerPortalOrderPhotoGroup,
  CustomerPortalPayload,
  CustomerPortalPhoto,
  CustomerPortalQuote,
  IResponseHttp,
} from '@core/models';
import {
  readNullableBoolean,
  readNumber,
  readString,
  readStringByKeys,
  toObject,
  unwrapResponse,
} from '@core/utils';
import { ApiService } from '../api/api.service';

@Injectable({
  providedIn: 'root',
})
export class CustomerPortalService {
  private readonly api = inject(ApiService);

  async getStatusVehicle(uuid: string): Promise<CustomerPortalPayload> {
    const response = await this.api.get<IResponseHttp | unknown>(
      ROUTES.PUBLIC_CUSTOMER_PORTAL_BY_UUID(uuid),
    );

    const payload = unwrapResponse(response);
    return this.normalizePayload(payload);
  }

  private normalizePayload(raw: unknown): CustomerPortalPayload {
    const source = toObject(raw);
    const vehicle = toObject(source['vehicle']);
    const customer = toObject(vehicle['customer']);
    const quotesRaw = Array.isArray(source['quotes']) ? source['quotes'] : [];
    const photosReceptionRaw = Array.isArray(source['photosReception'])
      ? source['photosReception']
      : [];
    const photosTechnicalReviewRaw = Array.isArray(source['photosTechnicalReview'])
      ? source['photosTechnicalReview']
      : [];
    const photosTheOrderRaw = Array.isArray(source['photosTheOrder'])
      ? source['photosTheOrder']
      : [];
    const pdfCheckListRaw = Array.isArray(source['pdfCheckList']) ? source['pdfCheckList'] : [];
    const photosReQuoteRaw = Array.isArray(source['photosReQuote'])
      ? source['photosReQuote']
      : [];

    return {
      id: readString(source, 'id'),
      status: readString(source, 'status') as CustomerPortalPayload['status'],
      currentKm: readNumber(source, 'currentKm'),
      fuelLevel: this.readOptionalNumber(source, ['fuelLevel', 'gasoline', 'fuel']),
      createdAt: readString(source, 'createdAt'),
      vehicle: {
        plate: readString(vehicle, 'plate'),
        customer: {
          name: readString(customer, 'name'),
        },
      },
      photosReception: this.normalizePhotos(photosReceptionRaw),
      photosTechnicalReview: this.normalizePhotos(photosTechnicalReviewRaw),
      photosTheOrder: this.normalizeOrderPhotoGroups(photosTheOrderRaw),
      pdfCheckList: this.normalizePhotos(pdfCheckListRaw),
      photosReQuote: this.normalizePhotos(photosReQuoteRaw),
      quotes: this.normalizeQuotes(quotesRaw),
      totalApproved: readNumber(source, 'totalApproved'),
    };
  }

  private normalizeQuotes(raw: unknown[]): CustomerPortalQuote[] {
    return raw.map((quote, index) => {
      const quoteObj = toObject(quote);
      const quoteItemsRaw = Array.isArray(quoteObj['items']) ? quoteObj['items'] : [];

      return {
        id: readString(quoteObj, 'id') || `${index}`,
        version: readNumber(quoteObj, 'version') || index + 1,
        total: readNumber(quoteObj, 'total'),
        approvedTotal: readNumber(quoteObj, 'approvedTotal'),
        status: readString(quoteObj, 'status') as CustomerPortalQuote['status'],
        items: quoteItemsRaw.map((quoteItem) => {
          const item = toObject(quoteItem);
          return {
            serviceName: readString(item, 'serviceName'),
            price: readNumber(item, 'price'),
            approved: readNullableBoolean(item, 'approved'),
            partPrice: this.readOptionalNumber(item, [
              'partPrice',
              'partsPrice',
              'replacementPrice',
              'sparePartPrice',
            ]),
          };
        }),
      };
    });
  }

  private normalizeOrderPhotoGroups(raw: unknown[]): CustomerPortalOrderPhotoGroup[] {
    return raw
      .map((group) => {
        const item = toObject(group);
        const photosSource = Array.isArray(item['photos']) ? item['photos'] : [];

        return {
          serviceOrderId: readString(item, 'serviceOrderId'),
          workItemId: readString(item, 'workItemId'),
          serviceName: readString(item, 'serviceName'),
          photos: this.normalizeOrderPhotos(photosSource),
        };
      })
      .filter((group) => group.photos.length > 0);
  }

  private normalizeOrderPhotos(raw: unknown[]): CustomerPortalOrderPhoto[] {
    return raw
      .map((photo, index) => {
        const item = toObject(photo);
        const url = readStringByKeys(item, ['url', 'photoUrl', 'src']);

        return {
          id: readString(item, 'id') || `${index}`,
          url,
          type: (readStringByKeys(item, ['type']) || 'PROCESS') as CustomerPortalOrderPhoto['type'],
          createdAt: readString(item, 'createdAt'),
        };
      })
      .filter((photo) => photo.url);
  }

  private normalizePhotos(raw: unknown[]): CustomerPortalPhoto[] {
    return raw
      .map((photo, index) => {
        const item = toObject(photo);
        const url = readStringByKeys(item, ['url', 'photoUrl', 'src']);

        return {
          id: readString(item, 'id') || `${index}`,
          url,
        };
      })
      .filter((photo) => photo.url);
  }

  private readOptionalNumber(source: Record<string, unknown>, keys: string[]): number | null {
    for (const key of keys) {
      const value = source[key];
      if (value === null || value === undefined || value === '') {
        continue;
      }

      const numberValue = Number(value);
      if (Number.isFinite(numberValue)) {
        return numberValue;
      }
    }

    return null;
  }
}
